import type { Rule, RuleItem, RuleItems, RuleValue } from "../../services/rule.tree.service";
import type { SearchCriteriaType } from "../../pojo/SearchCriteriaI";
import type { ItemService } from "../../util/items/item.service";
import { S25Util } from "../../util/s25-util";
import {
    INGESTION_FIELD_TYPE_ID,
    isNameResolvableFieldType,
    NAME_RESOLVABLE_FIELD_TYPES,
} from "./data.ingestion.const";
import type { IngestionFieldDefinition } from "./data.ingestion.service";

function isRuleItem(node: RuleItems | RuleItem): node is RuleItem {
    return node.type === "comp";
}

function walkRuleItems(
    node: RuleItems | RuleItem,
    visitRuleItem: (ruleItem: RuleItem) => void,
    visited: WeakSet<RuleItems | RuleItem> = new WeakSet(),
): void {
    if (visited.has(node)) {
        return;
    }
    visited.add(node);

    if (isRuleItem(node)) {
        visitRuleItem(node);
        return;
    }
    for (const child of node.children?.item ?? []) {
        walkRuleItems(child, visitRuleItem, visited);
    }
}

function extractRuleValueItemId(ruleValue: RuleValue): number | null {
    if (ruleValue.itemId != null) {
        const itemId = Number(ruleValue.itemId);
        return Number.isFinite(itemId) ? itemId : null;
    }
    const parsed = S25Util.parseInt(String(ruleValue.value ?? ""));
    return parsed != null && Number.isFinite(parsed) ? parsed : null;
}

function criteriaTypeForField(fieldDef: IngestionFieldDefinition): SearchCriteriaType["type"] | undefined {
    if (!isNameResolvableFieldType(fieldDef.fieldType)) {
        return undefined;
    }
    return NAME_RESOLVABLE_FIELD_TYPES[fieldDef.fieldType].searchCriteriaType;
}

async function loadNameMapsByCriteria(
    itemService: ItemService,
    idsByCriteria: Map<SearchCriteriaType["type"], Set<number>>,
): Promise<Map<SearchCriteriaType["type"], Map<number, string>>> {
    const nameMaps = new Map<SearchCriteriaType["type"], Map<number, string>>();

    await Promise.all(
        [...idsByCriteria.entries()].map(async ([criteriaType, itemIds]) => {
            if (!itemIds.size) {
                return;
            }

            const store = itemService.criteria(criteriaType);
            const items = await store.fetchAll([...itemIds], { fetch: { name: true } });
            const nameMap = new Map<number, string>();
            for (const item of items) {
                const itemId = Number(item.itemId);
                if (Number.isFinite(itemId)) {
                    nameMap.set(itemId, item.itemName ?? "");
                }
            }
            nameMaps.set(criteriaType, nameMap);
        }),
    );

    return nameMaps;
}

/**
 * Batch-resolves picker condition value ids to display names on ingestion show/hide rules.
 * Mutates the provided rules in place — pass a deep copy if the source is cached.
 */
export async function hydrateIngestionRuleConditionValueNames(
    rules: Rule[],
    fieldDefinitions: IngestionFieldDefinition[],
    itemService: ItemService,
): Promise<Rule[]> {
    if (!rules.length) {
        return rules;
    }

    const fieldById = new Map(
        fieldDefinitions
            .filter((fieldDef) => fieldDef.fieldDefinitionId != null)
            .map((fieldDef) => [fieldDef.fieldDefinitionId!, fieldDef]),
    );
    const idsByCriteria = new Map<SearchCriteriaType["type"], Set<number>>();

    const collectUnresolvedIds = (ruleItem: RuleItem) => {
        const sourceItem = ruleItem.sourceItem;
        if (sourceItem?.itemTypeId !== INGESTION_FIELD_TYPE_ID || !sourceItem.itemId) {
            return;
        }

        const fieldDef = fieldById.get(sourceItem.itemId);
        if (!fieldDef) {
            return;
        }

        const criteriaType = criteriaTypeForField(fieldDef);
        if (!criteriaType) {
            return;
        }

        let itemIds = idsByCriteria.get(criteriaType);
        if (!itemIds) {
            itemIds = new Set<number>();
            idsByCriteria.set(criteriaType, itemIds);
        }

        for (const ruleValue of ruleItem.val_obj ?? []) {
            if (ruleValue.itemName?.trim()) {
                continue;
            }
            const itemId = extractRuleValueItemId(ruleValue);
            if (itemId != null) {
                itemIds.add(itemId);
            }
        }
    };

    for (const rule of rules) {
        if (!rule.item) {
            continue;
        }
        walkRuleItems(rule.item, collectUnresolvedIds);
    }

    if (!idsByCriteria.size) {
        return rules;
    }

    const nameMaps = await loadNameMapsByCriteria(itemService, idsByCriteria);

    const applyResolvedNames = (ruleItem: RuleItem) => {
        const sourceItem = ruleItem.sourceItem;
        if (sourceItem?.itemTypeId !== INGESTION_FIELD_TYPE_ID || !sourceItem.itemId) {
            return;
        }

        const fieldDef = fieldById.get(sourceItem.itemId);
        if (!fieldDef) {
            return;
        }

        const criteriaType = criteriaTypeForField(fieldDef);
        if (!criteriaType) {
            return;
        }

        const nameMap = nameMaps.get(criteriaType);
        if (!nameMap?.size) {
            return;
        }

        for (const ruleValue of ruleItem.val_obj ?? []) {
            if (ruleValue.itemName?.trim()) {
                continue;
            }
            const itemId = extractRuleValueItemId(ruleValue);
            if (itemId == null) {
                continue;
            }
            const itemName = nameMap.get(itemId);
            if (itemName?.trim()) {
                ruleValue.itemName = itemName.trim();
            }
        }
    };

    for (const rule of rules) {
        if (!rule.item) {
            continue;
        }
        walkRuleItems(rule.item, applyResolvedNames);
    }

    return rules;
}
